import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ViewListIcon from '@material-ui/icons/ViewList';
import CategoryIcon from '@material-ui/icons/Category';
import { Alert } from '@material-ui/lab';
import { TabProps, TenantConfiguratorVariant, ApiCategory, ApiItem } from './types';
import { DEFAULT_APIS } from './defaults';

interface ApisTabProps extends TabProps {
  categories: ApiCategory[];
  restrictedCategories: ApiCategory[];
  variant?: TenantConfiguratorVariant;
}

const defaultApiSet = new Set(DEFAULT_APIS);

interface MergedItem extends ApiItem {
  restricted: boolean;
  category: string;
}

interface MergedCategory {
  name: string;
  items: MergedItem[];
}

/**
 * Merge unrestricted and restricted categories into a single list.
 * Items from restricted categories are tagged with restricted: true.
 */
function mergeCategories(
  unrestricted: ApiCategory[],
  restricted: ApiCategory[],
): MergedCategory[] {
  const categoryMap = new Map<string, MergedItem[]>();

  for (const cat of unrestricted) {
    const items = cat.items
      .filter(item => !defaultApiSet.has(item.api))
      .map(item => ({ ...item, restricted: false, category: cat.name }));
    if (items.length > 0) {
      categoryMap.set(cat.name, [...(categoryMap.get(cat.name) || []), ...items]);
    }
  }

  for (const cat of restricted) {
    const items = cat.items
      .filter(item => !defaultApiSet.has(item.api))
      .map(item => ({ ...item, restricted: true, category: cat.name }));
    if (items.length > 0) {
      categoryMap.set(cat.name, [...(categoryMap.get(cat.name) || []), ...items]);
    }
  }

  return Array.from(categoryMap.entries())
    .map(([name, items]) => ({ name, items }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function ApisTab({ config, onChange, categories, restrictedCategories, variant }: ApisTabProps) {
  const [viewMode, setViewMode] = useState<'category' | 'alphabetical'>('category');
  const [showRestricted, setShowRestricted] = useState(false);
  const selectedApis = new Set(config.additional_apis);

  // In edit mode, detect if environments have different API sets
  const envApisDiffer = useMemo(() => {
    if (variant !== 'edit' || config.environments.length <= 1) return false;
    const first = JSON.stringify([...config.environments[0].additional_apis].sort());
    return config.environments.some(
      env => JSON.stringify([...env.additional_apis].sort()) !== first,
    );
  }, [variant, config.environments]);

  const merged = useMemo(
    () => mergeCategories(categories, restrictedCategories),
    [categories, restrictedCategories],
  );

  // Build a lookup: api -> restricted boolean
  const restrictedApiSet = useMemo(() => {
    const s = new Set<string>();
    for (const cat of restrictedCategories) {
      for (const item of cat.items) {
        s.add(item.api);
      }
    }
    return s;
  }, [restrictedCategories]);

  const handleToggle = (api: string, checked: boolean) => {
    const updated = checked
      ? [...config.additional_apis, api]
      : config.additional_apis.filter(a => a !== api);
    onChange({ ...config, additional_apis: updated });
  };

  // All items flat + alphabetical for list view
  const allItems = useMemo(() => {
    const items: MergedItem[] = [];
    for (const cat of merged) {
      for (const item of cat.items) {
        if (!item.restricted || showRestricted) {
          items.push(item);
        }
      }
    }
    items.sort((a, b) => a.name.localeCompare(b.name));
    return items;
  }, [merged, showRestricted]);

  const totalSelected = config.additional_apis.length;
  const restrictedSelected = config.additional_apis.filter(api => restrictedApiSet.has(api)).length;

  return (
    <Box>
      {/* Edit mode warning about per-environment API differences */}
      {variant === 'edit' && envApisDiffer && (
        <Alert severity="warning" style={{ marginBottom: 8 }}>
          The existing tenant has different APIs configured per environment.
          Changing APIs here will apply the same set to all environments.
        </Alert>
      )}

      {/* Header: total count, restricted toggle, view toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 8 }}>
        <Typography variant="subtitle1">
          APIs ({totalSelected} selected{restrictedSelected > 0 && (
            <span style={{ color: '#d32f2f' }}>, {restrictedSelected} restricted</span>
          )})
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showRestricted}
                onChange={(_, checked) => setShowRestricted(checked)}
                color="secondary"
                size="small"
              />
            }
            label={<Typography variant="body2">Show Restricted</Typography>}
          />
          <Tooltip title="Category view">
            <IconButton
              size="small"
              color={viewMode === 'category' ? 'primary' : 'default'}
              onClick={() => setViewMode('category')}
            >
              <CategoryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Alphabetical list">
            <IconButton
              size="small"
              color={viewMode === 'alphabetical' ? 'primary' : 'default'}
              onClick={() => setViewMode('alphabetical')}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Category (accordion) view */}
      {viewMode === 'category' && merged.map(category => {
        const visibleItems = category.items.filter(
          item => !item.restricted || showRestricted,
        );
        // Always show category if it has selected restricted items
        const selectedRestrictedInCat = category.items.filter(
          item => item.restricted && selectedApis.has(item.api),
        );
        if (visibleItems.length === 0 && selectedRestrictedInCat.length === 0) return null;

        const selectedCount = visibleItems.filter(item => selectedApis.has(item.api)).length;
        const selectedInCat = category.items.filter(item => selectedApis.has(item.api));

        return (
          <ExpansionPanel key={category.name} defaultExpanded={false}>
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" flexDirection="column" style={{ width: '100%' }}>
                <Typography>
                  {category.name} ({selectedCount}/{visibleItems.length})
                </Typography>
                {/* Show selected APIs as chips below the category name */}
                {selectedInCat.length > 0 && (
                  <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginTop: 4 }}>
                    {selectedInCat.map(item => (
                      <Chip
                        key={item.api}
                        label={item.name}
                        size="small"
                        style={{
                          fontSize: '0.7rem',
                          height: 20,
                          backgroundColor: item.restricted ? '#ffebee' : '#e8f5e9',
                          color: item.restricted ? '#c62828' : '#2e7d32',
                          border: `1px solid ${item.restricted ? '#ef9a9a' : '#a5d6a7'}`,
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Box display="flex" flexDirection="column">
                {visibleItems.map(item => (
                  <FormControlLabel
                    key={item.api}
                    control={
                      <Checkbox
                        checked={selectedApis.has(item.api)}
                        onChange={(_, checked) => handleToggle(item.api, checked)}
                        color={item.restricted ? 'secondary' : 'primary'}
                        style={item.restricted ? { color: '#d32f2f' } : undefined}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        style={item.restricted ? { color: '#d32f2f' } : undefined}
                      >
                        {item.name}
                        {item.restricted && (
                          <Typography
                            component="span"
                            variant="caption"
                            style={{ color: '#d32f2f', marginLeft: 6, fontStyle: 'italic' }}
                          >
                            restricted
                          </Typography>
                        )}
                      </Typography>
                    }
                  />
                ))}
              </Box>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        );
      })}

      {/* Alphabetical list view */}
      {viewMode === 'alphabetical' && (
        <Box display="flex" flexDirection="column">
          {allItems.map(item => (
            <FormControlLabel
              key={item.api}
              control={
                <Checkbox
                  checked={selectedApis.has(item.api)}
                  onChange={(_, checked) => handleToggle(item.api, checked)}
                  color={item.restricted ? 'secondary' : 'primary'}
                  style={item.restricted ? { color: '#d32f2f' } : undefined}
                />
              }
              label={
                <span>
                  <Typography
                    component="span"
                    variant="body2"
                    style={item.restricted ? { color: '#d32f2f' } : undefined}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="textSecondary"
                    style={{ marginLeft: 8 }}
                  >
                    {item.category}
                  </Typography>
                  {item.restricted && (
                    <Typography
                      component="span"
                      variant="caption"
                      style={{ color: '#d32f2f', marginLeft: 6, fontStyle: 'italic' }}
                    >
                      restricted
                    </Typography>
                  )}
                </span>
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
